import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ReporterResource } from '../types';
import { formatDate as formatEasternDate } from '../utils/dateTime';

interface ResourcesListResponse { success: boolean; resources: ReporterResource[]; }
interface DownloadUrlResponse { success: boolean; downloadUrl?: string; }

// Format file size to human-readable string
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Format date to readable string (Eastern Time, per company standard)
const formatDate = (dateString: string): string =>
  formatEasternDate(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

// Check if a file type can be viewed in the browser
const isViewableType = (mimeType: string): boolean => {
  const viewableTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'text/plain',
    'text/html',
  ];
  return viewableTypes.includes(mimeType);
};

const ResourcesPage: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [resources, setResources] = useState<ReporterResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch resources when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchResources();
    }
  }, [isAuthenticated]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const response = await api.get<ResourcesListResponse>('/api/resources');
      if (response.success) {
        setResources(response.resources);
      } else {
        setErrorMessage('Failed to load resources');
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      setErrorMessage('An error occurred while fetching resources');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (resource: ReporterResource) => {
    setViewingId(resource.id);
    try {
      const response = await api.get<DownloadUrlResponse>(`/api/resources/${resource.id}/download`);

      if (response.success && response.downloadUrl) {
        // Open the file in a new tab for viewing
        window.open(response.downloadUrl, '_blank');
      } else {
        setErrorMessage('Failed to generate view link');
      }
    } catch (error) {
      console.error('Error viewing resource:', error);
      setErrorMessage('An error occurred while opening the file');
    } finally {
      setViewingId(null);
    }
  };

  const handleDownload = async (resource: ReporterResource) => {
    setDownloadingId(resource.id);
    try {
      const response = await api.get<DownloadUrlResponse>(`/api/resources/${resource.id}/download`);

      if (response.success && response.downloadUrl) {
        // Create a link to trigger download
        const link = document.createElement('a');
        link.href = response.downloadUrl;
        link.download = resource.fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setErrorMessage('Failed to generate download link');
      }
    } catch (error) {
      console.error('Error downloading resource:', error);
      setErrorMessage('An error occurred while downloading the file');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCloseMessage = () => {
    setErrorMessage(null);
  };

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography variant="h6" ml={2}>
          Checking authentication status...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login page via useEffect
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Resources
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Access helpful documents, guides, and templates.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : resources.length === 0 ? (
          <Alert severity="info">No resources are currently available.</Alert>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resources.map((resource, index) => (
                  <TableRow
                    key={resource.id}
                    hover
                    sx={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                      '&:hover': { backgroundColor: '#f0f7ff' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {resource.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {resource.fileName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {resource.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatFileSize(resource.fileSize)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(resource.updatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {isViewableType(resource.mimeType) && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            startIcon={
                              viewingId === resource.id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <VisibilityIcon />
                              )
                            }
                            onClick={() => handleView(resource)}
                            disabled={viewingId === resource.id || downloadingId === resource.id}
                            sx={{ minWidth: '100px' }}
                          >
                            {viewingId === resource.id ? 'Opening...' : 'View'}
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          startIcon={
                            downloadingId === resource.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DownloadIcon />
                            )
                          }
                          onClick={() => handleDownload(resource)}
                          disabled={downloadingId === resource.id || viewingId === resource.id}
                          sx={{ minWidth: '110px' }}
                        >
                          {downloadingId === resource.id ? 'Loading...' : 'Download'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
      <Footer />

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseMessage} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResourcesPage;
